import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { SKIP_IDEMPOTENCY, SUPPRESS_ERROR_TOAST } from '../../../core/constants/context-tokens';
import { HelpChatService } from '../../../openapi';

/** A spoken question as the service understood it, with its answer. */
export interface VoiceChatAnswer {
  /** The transcript of the recording. */
  question: string;
  answer: string;
  /** Code of the language the question was recognised in, when known. */
  languageCode: string | null;
}

/**
 * The AI help chat, through the PreCalc backend (`/api/HelpChat`), which proxies the RAG service.
 *
 * A thin wrapper over the generated client that marks every request as quiet: a failure is shown in
 * the conversation rather than as a toast, and a question is not a save, so it must not disable the
 * application's save buttons for as long as the answer takes.
 */
@Injectable({ providedIn: 'root' })
export class HelpChatApiService {
  private readonly _api = inject(HelpChatService);

  /** Whether the backend has a chat service configured. */
  status(): Observable<boolean> {
    return this._api.apiHelpChatStatusGet('body', false, { context: quiet() }).pipe(map((s) => !!s?.enabled));
  }

  /** Asks a typed question; emits the answer as markdown. */
  ask(question: string): Observable<string> {
    return this._api
      .apiHelpChatAskPost({ question }, 'body', false, { context: quiet() })
      .pipe(map((response) => response?.answer ?? ''));
  }

  /** Asks a recorded question. The backend has it transcribed and answers the transcript. */
  askVoice(recording: Blob, languageCode?: string): Observable<VoiceChatAnswer> {
    return this._api.apiHelpChatAskVoicePost(recording, languageCode, 'body', false, { context: quiet() }).pipe(
      map((response) => ({
        question: (response?.question ?? '').trim(),
        answer: response?.answer ?? '',
        languageCode: response?.languageCode || null,
      }))
    );
  }

  /** Reads a text aloud; emits the MP3. */
  speak(text: string, languageCode?: string): Observable<Blob> {
    // The generated signature names the payload FileResult (the .NET return type); on the wire it is
    // the MP3 body, which the generated client reads as a Blob because of the audio/mpeg accept header.
    return this._api
      .apiHelpChatSpeakPost({ text, languageCode: languageCode || null }, 'body', false, { context: quiet() })
      .pipe(map((body) => body as unknown as Blob));
  }
}

function quiet(): HttpContext {
  return new HttpContext().set(SUPPRESS_ERROR_TOAST, true).set(SKIP_IDEMPOTENCY, true);
}
